import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { FileText } from "lucide-react";
import BatchMaterialsPage from "./BatchMaterialsPage";
import { EmptyState } from "../ui";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";

/**
 * StudentMaterialsPage
 *
 * Standalone page for students to browse batch + course-level material
 * uploaded by admins/trainers. Resolves the student's batchId and courseId
 * automatically from their profile.
 *
 * Route: /s/materials
 */
const StudentMaterialsPage = () => {
  const user = getCurrentUser();
  const studentId = user?.studentId;

  const [batchId, setBatchId] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStudent = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await apiClient.get(`/students/${studentId}`);
        const student = data.student;
        if (student?.batch) {
          const bId = student.batch._id || student.batch;
          setBatchId(bId);
          // course may be populated object or just an id
          const cId = student.batch?.course?._id || student.batch?.course || student.course?._id || student.course;
          setCourseId(cId || null);
        } else {
          setError("You are not enrolled in any batch yet.");
        }
      } catch {
        setError("Failed to load your batch information.");
      }
      setLoading(false);
    };

    if (studentId) {
      fetchStudent();
    } else {
      setLoading(false);
      setError("Student information not found.");
    }
  }, [studentId]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !batchId) {
    return (
      <div className="px-3 py-4">
        <EmptyState
          icon={<FileText size={40} />}
          title="No Materials"
          description={error || "You are not enrolled in any batch yet."}
        />
      </div>
    );
  }

  return (
      <BatchMaterialsPage batchId={batchId} courseId={courseId} role="student" />
  );
};

export default StudentMaterialsPage;
